import {clsx} from 'clsx'
import {useSessions} from '@github-ui/agent-sessions/hooks/useSessions'
import {SessionState} from '@github-ui/agent-sessions/types/session'
import type {Pull} from '@github-ui/agent-sessions/types/pull'
import type {Repository} from '@github-ui/current-repository'
import {CurrentRepositoryProvider, useCurrentRepository} from '@github-ui/current-repository'
import {PullContextProvider, usePullContext} from '@github-ui/agent-sessions/contexts/PullContext'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {Link} from '@primer/react'
import {ArrowBothIcon} from '@primer/octicons-react'
import styles from './CopilotCodingAgentStatus.module.css'
import {
  getSessionStatusIcon,
  getSessionStatusText,
  getSessionTimeDescription,
  generateSessionLink,
} from './CopilotCodingAgentSessionHelper'

function CopilotCodingAgentStatusWidget({
  useMockData,
  sessionsPollingInterval,
}: {
  useMockData: boolean
  sessionsPollingInterval: number
}) {
  const {
    data: sessionsData,
    isLoading: sessionsIsLoading,
    isError: sessionsIsError,
  } = useSessions({
    initialData: undefined,
    useMockData,
    sessionsPollingInterval,
  })
  const {ownerLogin, name: repoName} = useCurrentRepository()
  const {
    pull: {number: pullNumber},
  } = usePullContext()

  const activeSession = sessionsData?.at(-1)
  const sessionHref = generateSessionLink(ownerLogin, repoName, pullNumber, activeSession?.id ?? null)

  if (sessionsIsLoading) {
    return (
      <div
        className={clsx(
          styles.copilotCard,
          'fgColor-default rounded-2 no-underline mb-3 border d-flex flex-items-stretch',
        )}
        data-testid="agent-status-loading-skeleton"
      >
        <div
          className="mr-2 d-flex flex-column flex-content-start flex-items-start circle Skeleton"
          style={{height: '16px', width: '16px'}}
        >
          &nbsp;
        </div>
        <div className="flex-1">
          <p className="mb-0 Skeleton Skeleton--text">&nbsp;</p>
          <p className="mb-0 Skeleton Skeleton--text">&nbsp;</p>
        </div>
        <div className="ml-3 d-flex flex-column flex-justify-start">
          <div className="flex-self-start" style={{height: '16px', width: '16px'}} />
        </div>
      </div>
    )
  }

  if (sessionsIsError) {
    return (
      <div
        className={clsx(
          styles.copilotCard,
          'fgColor-default rounded-2 no-underline mb-3 border d-flex flex-items-stretch',
        )}
      >
        <div className="mr-2 fgColor-muted d-flex flex-column flex-content-start flex-items-start">
          {getSessionStatusIcon(SessionState.Failed)}
        </div>
        <div className="flex-1">
          <p className="mb-0 fgColor-default text-semibold">Error loading sessions</p>
          <p className="mb-0 fgColor-muted text-small">Retrying...</p>
        </div>
        <div className="ml-3 d-flex flex-column flex-justify-start">
          <div className="flex-self-start" style={{height: '16px', width: '16px'}} />
        </div>
      </div>
    )
  }

  return (
    <Link
      href={sessionHref}
      className={clsx(
        styles.copilotCard,
        'fgColor-default rounded-2 no-underline mb-3 border d-flex flex-items-stretch',
      )}
    >
      <div className="mr-2 fgColor-muted d-flex flex-column flex-content-start flex-items-start">
        {getSessionStatusIcon(activeSession?.state ?? SessionState.Idle)}
      </div>

      <div className="flex-1">
        <p className="mb-0 fgColor-default text-semibold">
          {getSessionStatusText(activeSession?.state ?? SessionState.Idle)}
        </p>
        <p className="mb-0 fgColor-muted text-small">
          {activeSession &&
            getSessionTimeDescription(
              activeSession?.state,
              activeSession?.created_at,
              activeSession?.completed_at ?? '',
            )}
        </p>
      </div>

      <div className="ml-3 d-flex flex-column flex-justify-start">
        <div className="flex-self-start">
          <ArrowBothIcon className={`${styles.arrowBoth} fgColor-muted`} />
        </div>
      </div>
    </Link>
  )
}

export interface CopilotCodingAgentStatusProps {
  repository: Repository
  pull: Pull
  useMockData: boolean
  sessionsPollingInterval: number
}

export function CopilotCodingAgentStatus({
  repository,
  pull,
  useMockData,
  sessionsPollingInterval,
}: CopilotCodingAgentStatusProps) {
  return (
    <CurrentRepositoryProvider repository={repository}>
      <ConsecutiveCAPI401sProvider>
        <PullContextProvider pull={pull}>
          <CopilotCodingAgentStatusWidget useMockData={useMockData} sessionsPollingInterval={sessionsPollingInterval} />
        </PullContextProvider>
      </ConsecutiveCAPI401sProvider>
    </CurrentRepositoryProvider>
  )
}

try{ CopilotCodingAgentStatusWidget.displayName ||= 'CopilotCodingAgentStatusWidget' } catch {}
try{ CopilotCodingAgentStatus.displayName ||= 'CopilotCodingAgentStatus' } catch {}